import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Box,
  CircularProgress,
  Divider,
  Tooltip,
  Fade,
  Snackbar,
  Alert,
  Link,
  useTheme
} from '@mui/material';
import { 
  NotificationsNone, 
  NotificationsActive, 
  CheckCircleOutline,
  ErrorOutline,
  DeleteSweep,
  ChevronRight,
  ArrowForward
} from '@mui/icons-material';
import { useAuthToken } from "../TokenContext";
import moment from 'moment';
import { useIntl } from "react-intl";

// Constants
const API_ENDPOINTS = {
  NOTIFICATIONS: process.env.REACT_APP_CLIENT_CLIENTTASKNOTIFICATION,
  MARK_READ: process.env.REACT_APP_TALENT_NOTIFICATIONREAD,
  POD_AUTOMATION: process.env.REACT_APP_CLIENT_PODAUTOMATIONNOTIFICATION
};

// Maximum number of notifications to show in dropdown
const MAX_NOTIFICATIONS_DISPLAY = 10;

// Notification Item Component
const NotificationItem = ({ notification, onMarkAsRead, isRemoved, onNotificationClick }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Get priority color based on notification type or priority if available
  const getPriorityColor = () => {
    // For pod automation notifications
    if (notification.type === 'APPROVAL_REQUIRED') {
      return '#ff9800'; // Orange for approvals
    }
    
    // For regular notifications
    if (notification.type === 'urgent' || notification.priority === 'high') {
      return '#f44336'; // Red for urgent
    } else if (notification.type === 'important' || notification.priority === 'medium') {
      return '#ff9800'; // Orange for important
    }
    return '#2196f3'; // Default blue
  };

  const borderColor = getPriorityColor();
  
  const handleMarkAsReadClick = (e) => {
    e.stopPropagation();
    onMarkAsRead(notification.id);
  };

  const handleItemClick = (e) => {
    e.preventDefault();
    onNotificationClick(notification);
  };
  const intl = useIntl();

  return (
    <ListItem
      onClick={handleItemClick}
      sx={{
        transition: 'all 0.3s ease-in-out',
        transform: isRemoved ? 'translateX(100%)' : 'translateX(0)',
        opacity: isRemoved ? 0 : 1,
        maxHeight: isRemoved ? 0 : 'auto',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        borderLeft: `4px solid ${borderColor}`,
        backgroundColor: notification.read 
          ? isDarkMode 
            ? 'rgba(255, 255, 255, 0.05)' 
            : 'rgba(0, 0, 0, 0.03)'
          : isDarkMode
            ? 'rgba(144, 224, 239, 0.15)'
            : 'rgba(144, 224, 239, 0.1)',
        my: 1,
        borderRadius: 1,
        boxShadow: isDarkMode 
          ? '0 1px 3px rgba(255,255,255,0.1)' 
          : '0 1px 3px rgba(0,0,0,0.12)',
        '&:hover': {
          backgroundColor: notification.read 
            ? isDarkMode 
              ? 'rgba(255, 255, 255, 0.08)' 
              : 'rgba(0, 0, 0, 0.05)'
            : isDarkMode
              ? 'rgba(144, 224, 239, 0.25)'
              : 'rgba(144, 224, 239, 0.2)',
          cursor: 'pointer'
        },
        p: 2
      }}
    >
      <ListItemText
        primary={
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? 'grey.100' : 'text.primary',
              fontWeight: notification.read ? 'normal' : 'bold',
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span>{notification.message}</span>
            <ChevronRight 
              fontSize="small" 
              sx={{ color: isDarkMode ? 'grey.400' : 'action.active' }}
            />
          </Typography>
        }
        secondary={
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block',
              mt: 0.5,
              color: isDarkMode ? 'grey.400' : 'text.secondary'
            }}
          >
            {notification.timeAgo}
          </Typography>
        }
      />
      {!notification.read && !notification.isPodAutomation && (
        <Tooltip title="Mark as read" placement="bottom" TransitionComponent={Fade}>
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircleOutline />}
            sx={{
              bgcolor: isDarkMode ? '#1976d2' : '#90E0EF',
              color: isDarkMode ? '#fff' : '#0077b6',
              fontWeight: 'medium',
              '&:hover': {
                bgcolor: isDarkMode ? '#1565c0' : '#48cae4'
              },
              '&.clicked': {
                transform: 'scale(0.95)'
              },
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0,0,0,0.3)' 
                : '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease'
            }}
            onClick={handleMarkAsReadClick}
          >
            Mark as Read
          </Button>
        </Tooltip>
      )}
    </ListItem>
  );
};

const NotificationManagerIcon = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { user } = useSelector((state) => state.auth);
  const token = useAuthToken();
  const history = useHistory();
  const intl = useIntl();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Fetch regular notifications
  const fetchRegularNotifications = async () => {
    if (!token || !user?.data?.[0]?.emp_id) {
      console.log('Token or user data is missing for regular notifications');
      return [];
    }

    try {
      const response = await fetch(API_ENDPOINTS.NOTIFICATIONS, {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ client_id: user.data[0].emp_id }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch regular notifications');
      }

      const { data } = await response.json();
      
      if (!data || !Array.isArray(data)) {
        return [];
      }
      
      return data.map(notification => ({
        ...notification,
        timeAgo: moment(notification.createdAt).fromNow(),
        read: notification.notification_read === 1,
        isPodAutomation: false,
        sortDate: new Date(notification.createdAt)
      }));
    } catch (error) {
      console.error('Error fetching regular notifications:', error);
      return [];
    }
  };

  // Fetch pod automation notifications
  const fetchPodAutomationNotifications = async () => {
    if (!user?.data?.[0]?.emp_id) {
      console.log('User data is missing for pod automation notifications');
      return [];
    }

    try {
      const response = await fetch(API_ENDPOINTS.POD_AUTOMATION, {
        method: 'POST',
      headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ manager_id: user.data[0].emp_id }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pod automation notifications');
      }

      const { body } = await response.json();
      
      if (!body || !Array.isArray(body)) {
        return [];
      }
      
      // Transform pod automation notifications to match regular notification structure
      return body.map((notification, index) => ({
        id: `pod_auto_${index}_${Date.now()}`, // Generate unique ID
        message: notification.message,
        type: notification.type,
        status: notification.status,
        read: notification.status !== 'pending', // Mark as unread if pending
        timeAgo: 'Just now', // Since these don't have timestamps
        isPodAutomation: true,
        sortDate: new Date() // Use current date for sorting
      }));
    } catch (error) {
      console.error('Error fetching pod automation notifications:', error);
      return [];
    }
  };

  // Combined fetch function
  const fetchNotifications = useCallback(async () => {
    if (!token || !user?.data?.[0]?.emp_id) {
      console.log('Token or user data is missing');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch both notification types in parallel
      const [regularNotifs, podAutoNotifs] = await Promise.all([
        fetchRegularNotifications(),
        fetchPodAutomationNotifications()
      ]);

      // Combine and sort notifications
      const combinedNotifications = [...regularNotifs, ...podAutoNotifs]
        .sort((a, b) => b.sortDate - a.sortDate);

      setAllNotifications(combinedNotifications);
      setNotifications(combinedNotifications.slice(0, MAX_NOTIFICATIONS_DISPLAY));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Could not load notifications. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  // Initial fetch when component mounts
  useEffect(() => {
    fetchNotifications();
    
    // Refresh notifications periodically (every 5 minutes)
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [fetchNotifications]);
  
  // Additional fetch when dropdown opens
  useEffect(() => {
    if (anchorEl) {
      fetchNotifications();
    }
  }, [fetchNotifications, anchorEl]);

  const handleMarkAsRead = async (notificationId) => {
    // Skip if this is a pod automation notification (they don't support mark as read)
    const notification = allNotifications.find(n => n.id === notificationId);
    if (notification?.isPodAutomation) {
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.MARK_READ, {
        method: 'PUT',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notification_ids: [notificationId] }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      setRemovedIds(prev => [...prev, notificationId]);
      setAllNotifications(prev => 
        prev.map(note => 
          note.id === notificationId ? { ...note, read: true } : note
        )
      );
      setNotifications(prev => 
        prev.map(note => 
          note.id === notificationId ? { ...note, read: true } : note
        )
      );
      
      setSnackbar({
        open: true,
        message: 'Notification marked as read',
        severity: 'success'
      });
      
      // Refresh notifications after a short delay
      setTimeout(() => {
        fetchNotifications();
      }, 1000);
      
    } catch (error) {
      console.error('Error marking notification as read:', error);
      setSnackbar({
        open: true,
        message: 'Could not mark notification as read',
        severity: 'error'
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadIds = notifications
      .filter(note => !note.read && !note.isPodAutomation) // Only regular notifications
      .map(note => note.id);
    
    if (unreadIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'No unread notifications',
        severity: 'info'
      });
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.MARK_READ, {
        method: 'PUT',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notification_ids: unreadIds }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }

      setRemovedIds(prev => [...prev, ...unreadIds]);
      setAllNotifications(prev => 
        prev.map(note => 
          note.isPodAutomation ? note : { ...note, read: true }
        )
      );
      setNotifications(prev => 
        prev.map(note => 
          note.isPodAutomation ? note : { ...note, read: true }
        )
      );
      
      setSnackbar({
        open: true,
        message: `${unreadIds.length} notification${unreadIds.length > 1 ? 's' : ''} marked as read`,
        severity: 'success'
      });
      
      // Refresh notifications after a short delay
      setTimeout(() => {
        fetchNotifications();
      }, 1000);
      
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      setSnackbar({
        open: true,
        message: 'Could not mark all notifications as read',
        severity: 'error'
      });
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications(); // Refresh on open
  };

  const handleClose = () => {
    setAnchorEl(null);
    setRemovedIds([]); // Reset removed IDs when closing
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleNotificationClick = (notification) => {
    // Mark notification as read if it's not already read (only for regular notifications)
    if (!notification.read && !notification.isPodAutomation) {
      handleMarkAsRead(notification.id);
    }

    // Close the dropdown
    handleClose();

    // Navigate based on notification type
    if (notification.isPodAutomation) {
      // Handle pod automation notifications
      if (notification.type === 'APPROVAL_REQUIRED') {
        // Navigate to approval page or relevant pod automation page
        if (notification.message.includes('PLAN_SPRINT')) {
          history.push('/AgentsList');
        } else if (notification.message.includes('CREATE_STORIES')) {
          history.push('/AgentsList');
        } else {
          // Default pod automation page
          history.push('/AgentsList');
        }
      }
    } else {
      // Handle regular notifications
      if (notification.message.toLowerCase().includes('task')) {
        history.push('/ViewPodTasksManagerbyid');
      } else if (notification.message.toLowerCase().includes('checked in code')) {
        history.push('/PodMetricsmanager');
      } else {
        history.push('/client-dashboard');
      }
    }
  };

  const handleViewAllClick = () => {
    // Close the dropdown
    handleClose();
    
    // Navigate to notifications history page
    history.push('/NotificationsHistorymanager');
  };

  const open = Boolean(anchorEl);
  const unreadCount = allNotifications.filter(note => !note.read).length;

  return (
    <Box>
      <Tooltip title= {intl.formatMessage({ id: "notifications" })} placement="bottom">
        <IconButton 
          onClick={handleClick} 
          sx={{ 
            color: unreadCount > 0 
              ? '#0077b6' 
              : isDarkMode 
                ? 'grey.400' 
                : 'text.secondary',
            ml: 1,
            transition: 'transform 0.2s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              bgcolor: isDarkMode 
                ? 'rgba(0, 119, 182, 0.15)' 
                : 'rgba(0, 119, 182, 0.08)'
            }
          }}
        >
          <Badge 
            badgeContent={unreadCount} 
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                height: '1.3rem',
                minWidth: '1.3rem',
                padding: '0 4px',
                top: 4,
                right: 4
              }
            }}
          >
            {unreadCount > 0 ? <NotificationsActive /> : <NotificationsNone />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: 350 },
            maxWidth: { xs: '95vw', sm: 350 },
            maxHeight: 500,
            borderRadius: 2,
            boxShadow: isDarkMode 
              ? '0 4px 20px rgba(0,0,0,0.6)' 
              : '0 4px 20px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            bgcolor: isDarkMode ? 'grey.900' : 'background.paper'
          }
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 2,
            bgcolor: isDarkMode ? 'grey.800' : '#f8f9fa'
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 'bold', 
              fontSize: '1rem',
              color: isDarkMode ? 'grey.100' : 'text.primary'
            }}
          >
            {intl.formatMessage({ id: "notifications" })}
          </Typography>
          
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <Button
                size="small"
                startIcon={<DeleteSweep />}
                onClick={handleMarkAllAsRead}
                sx={{ 
                  fontSize: '0.75rem',
                  color: '#0077b6',
                  '&:hover': {
                    bgcolor: isDarkMode 
                      ? 'rgba(0, 119, 182, 0.15)' 
                      : 'rgba(0, 119, 182, 0.08)'
                  }
                }}
              >
                {intl.formatMessage({ id: "markAllAsRead" })}
              </Button>
            </Tooltip>
          )}
        </Box>
        
        <Divider sx={{ bgcolor: isDarkMode ? 'grey.700' : 'divider' }} />
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} sx={{ color: '#0077b6' }} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <ErrorOutline color="error" sx={{ mb: 1, fontSize: '2rem' }} />
            <Typography color="error">{error}</Typography>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={fetchNotifications}
              sx={{ mt: 2 }}
            >
              {intl.formatMessage({ id: "tryAgain" })}
            </Button>
          </Box>
        ) : (
          <>
            <List sx={{ p: 0, maxHeight: 350, overflow: 'auto' }}>
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onNotificationClick={handleNotificationClick}
                    isRemoved={removedIds.includes(notification.id)}
                  />
                ))
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <CheckCircleOutline 
                    sx={{ 
                      fontSize: '3rem', 
                      color: isDarkMode ? '#64b5f6' : '#90E0EF', 
                      mb: 2 
                    }} 
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ color: isDarkMode ? 'grey.400' : 'text.secondary' }}
                  >
                   {intl.formatMessage({ id: "noNotificationsToDisplay" })}
                  </Typography>
                </Box>
              )}
            </List>
            
            {/* View More Link */}
            {allNotifications.length > MAX_NOTIFICATIONS_DISPLAY && (
              <Box 
                sx={{ 
                  p: 2, 
                  borderTop: isDarkMode 
                    ? '1px solid rgba(255, 255, 255, 0.12)' 
                    : '1px solid rgba(0, 0, 0, 0.12)', 
                  textAlign: 'center',
                  backgroundColor: isDarkMode 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.02)'
                }}
              >
                <Button
                  endIcon={<ArrowForward />}
                  onClick={handleViewAllClick}
                  sx={{
                    color: '#0077b6',
                    fontWeight: 'medium',
                    textTransform: 'none'
                  }}
                >
                  {intl.formatMessage({ id: "viewAllNotifications" })} ({allNotifications.length})
                </Button>
              </Box>
            )}
          </>
        )}
      </Popover>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationManagerIcon;